import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CatalogService, { CatalogScopeItem } from '../services/CatalogService';

// FE-BILL-R31-1: Draft item type for new catalog items
export interface DraftScopeItem {
  text: string;
  sort_order: number;
}

interface ScopeSubtasksSectionProps {
  catalogItemId: number | null;
  // FE-BILL-R31-1: Draft mode props
  draftMode?: boolean;
  draftItems?: DraftScopeItem[];
  onDraftItemsChange?: (items: DraftScopeItem[]) => void;
}

export default function ScopeSubtasksSection({
  catalogItemId,
  draftMode = false,
  draftItems = [],
  onDraftItemsChange,
}: ScopeSubtasksSectionProps): React.JSX.Element {
  const [scopeItems, setScopeItems] = useState<CatalogScopeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newText, setNewText] = useState('');

  const loadScopeItems = useCallback(async () => {
    if (!catalogItemId) {
      setScopeItems([]);
      return;
    }

    setLoading(true);
    try {
      const response = await CatalogService.fetchScopeItems(catalogItemId);
      // FE-BILL-HF-R30-5: Tolerant response parsing
      // Handle: Array | { scope_items: [...] } | { results: [...] } | undefined
      const raw = response as unknown;
      let items: CatalogScopeItem[];
      if (Array.isArray(raw)) {
        items = raw;
      } else if (raw && typeof raw === 'object') {
        const obj = raw as Record<string, unknown>;
        items = (obj.scope_items || obj.results || []) as CatalogScopeItem[];
      } else {
        items = [];
      }
      // FE-BILL-HF-R30-5: Client-side sorting by sort_order, then by id
      items.sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        return a.id - b.id;
      });
      setScopeItems(items);
    } catch (err) {
      console.error('Failed to load scope items:', err);
      setScopeItems([]);
    } finally {
      setLoading(false);
    }
  }, [catalogItemId]);

  useEffect(() => {
    loadScopeItems();
  }, [loadScopeItems]);

  const handleAddItem = async () => {
    if (!catalogItemId || !newText.trim()) return;

    setAddingNew(true);
    try {
      const maxSortOrder = scopeItems.length > 0
        ? Math.max(...scopeItems.map((s) => s.sort_order))
        : 0;

      const created = await CatalogService.createScopeItem(catalogItemId, {
        text: newText.trim(),
        sort_order: maxSortOrder + 10,
      });

      setScopeItems((prev) => [...prev, created]);
      setNewText('');
      toast.success('Punkt hinzugefügt');
    } catch (err: any) {
      console.error('Failed to create scope item:', err);
      const msg = err?.response?.data?.detail || 'Fehler beim Hinzufügen';
      toast.error(msg);
    } finally {
      setAddingNew(false);
    }
  };

  const handleUpdateText = async (item: CatalogScopeItem, newTextValue: string) => {
    if (newTextValue.trim() === item.text) return;

    try {
      const updated = await CatalogService.updateScopeItem(item.id, {
        text: newTextValue.trim(),
      });
      setScopeItems((prev) =>
        prev.map((s) => (s.id === item.id ? updated : s))
      );
    } catch (err: any) {
      console.error('Failed to update scope item:', err);
      const msg = err?.response?.data?.detail || 'Fehler beim Speichern';
      toast.error(msg);
    }
  };

  const handleDelete = async (item: CatalogScopeItem) => {
    setScopeItems((prev) => prev.filter((s) => s.id !== item.id));

    try {
      await CatalogService.deleteScopeItem(item.id);
      toast.success('Punkt gelöscht');
    } catch (err: any) {
      console.error('Failed to delete scope item:', err);
      setScopeItems((prev) => [...prev, item].sort((a, b) => a.sort_order - b.sort_order));
      const msg = err?.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(msg);
    }
  };

  const handleMoveUp = async (item: CatalogScopeItem, index: number) => {
    if (index === 0) return;

    const prevItem = scopeItems[index - 1];
    const newOrder = [...scopeItems];
    newOrder[index - 1] = item;
    newOrder[index] = prevItem;
    setScopeItems(newOrder);

    try {
      await Promise.all([
        CatalogService.updateScopeItem(item.id, { sort_order: prevItem.sort_order }),
        CatalogService.updateScopeItem(prevItem.id, { sort_order: item.sort_order }),
      ]);
    } catch (err) {
      console.error('Failed to reorder:', err);
      loadScopeItems();
      toast.error('Fehler beim Sortieren');
    }
  };

  const handleMoveDown = async (item: CatalogScopeItem, index: number) => {
    if (index === scopeItems.length - 1) return;

    const nextItem = scopeItems[index + 1];
    const newOrder = [...scopeItems];
    newOrder[index + 1] = item;
    newOrder[index] = nextItem;
    setScopeItems(newOrder);

    try {
      await Promise.all([
        CatalogService.updateScopeItem(item.id, { sort_order: nextItem.sort_order }),
        CatalogService.updateScopeItem(nextItem.id, { sort_order: item.sort_order }),
      ]);
    } catch (err) {
      console.error('Failed to reorder:', err);
      loadScopeItems();
      toast.error('Fehler beim Sortieren');
    }
  };

  // FE-BILL-R31-1: Draft mode handlers
  const handleDraftAdd = () => {
    if (!newText.trim() || !onDraftItemsChange) return;

    // Support multi-line paste: split by newlines
    const lines = newText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const maxOrder = draftItems.length > 0
      ? Math.max(...draftItems.map(d => d.sort_order))
      : 0;

    const newItems = lines.map((line, idx) => ({
      text: line,
      sort_order: maxOrder + (idx + 1) * 10,
    }));

    onDraftItemsChange([...draftItems, ...newItems]);
    setNewText('');
  };

  const handleDraftDelete = (index: number) => {
    if (!onDraftItemsChange) return;
    const updated = draftItems.filter((_, i) => i !== index);
    onDraftItemsChange(updated);
  };

  const handleDraftMoveUp = (index: number) => {
    if (index === 0 || !onDraftItemsChange) return;
    const updated = [...draftItems];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    // Reassign sort_order
    updated.forEach((item, i) => { item.sort_order = (i + 1) * 10; });
    onDraftItemsChange(updated);
  };

  const handleDraftMoveDown = (index: number) => {
    if (index === draftItems.length - 1 || !onDraftItemsChange) return;
    const updated = [...draftItems];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    // Reassign sort_order
    updated.forEach((item, i) => { item.sort_order = (i + 1) * 10; });
    onDraftItemsChange(updated);
  };

  const handleDraftTextChange = (index: number, newTextValue: string) => {
    if (!onDraftItemsChange) return;
    const updated = [...draftItems];
    updated[index] = { ...updated[index], text: newTextValue };
    onDraftItemsChange(updated);
  };

  // FE-BILL-R31-1: Render draft mode UI
  if (draftMode) {
    return (
      <div data-testid="catalog-scope-section" style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Leistungsumfang (Subtasks)</h4>

        {draftItems.length === 0 ? (
          <p style={emptyStateStyle}>
            Noch keine Subtasks. Füge Punkte hinzu, damit sie später in Angebot und Job-Checkliste erscheinen.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {draftItems.map((item, index) => (
              <div
                key={`draft-${index}`}
                data-testid={`catalog-scope-draft-item-${index}`}
                style={itemRowStyle}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button
                    type="button"
                    onClick={() => handleDraftMoveUp(index)}
                    disabled={index === 0}
                    style={{
                      ...arrowButtonStyle,
                      opacity: index === 0 ? 0.3 : 1,
                    }}
                    title="Nach oben"
                  >
                    <FaArrowUp size={10} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDraftMoveDown(index)}
                    disabled={index === draftItems.length - 1}
                    style={{
                      ...arrowButtonStyle,
                      opacity: index === draftItems.length - 1 ? 0.3 : 1,
                    }}
                    title="Nach unten"
                  >
                    <FaArrowDown size={10} />
                  </button>
                </div>

                <input
                  type="text"
                  value={item.text}
                  onChange={(e) => handleDraftTextChange(index, e.target.value)}
                  style={textInputStyle}
                />

                <button
                  type="button"
                  data-testid={`catalog-scope-draft-delete-${index}`}
                  onClick={() => handleDraftDelete(index)}
                  style={deleteButtonStyle}
                  title="Löschen"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Neuer Punkt... (mehrere Zeilen möglich)"
            style={{ ...textInputStyle, flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && newText.trim()) {
                e.preventDefault();
                handleDraftAdd();
              }
            }}
          />
          <button
            type="button"
            data-testid="catalog-scope-draft-add"
            onClick={handleDraftAdd}
            disabled={!newText.trim()}
            style={{
              ...addButtonStyle,
              opacity: !newText.trim() ? 0.5 : 1,
            }}
          >
            <FaPlus size={12} />
            <span>Hinzufügen</span>
          </button>
        </div>
      </div>
    );
  }

  // FE-BILL-HF-R30-4: Show disabled state for new items (before first save) - when NOT in draft mode
  if (!catalogItemId) {
    return (
      <div data-testid="catalog-scope-section" style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Leistungsumfang (Subtasks)</h4>
        <div
          data-testid="catalog-scope-new-item-hint"
          style={{
            padding: '16px',
            background: '#f8f9fb',
            borderRadius: '6px',
            border: '1px dashed #d1d5db',
            textAlign: 'center',
            opacity: 0.7,
          }}
        >
          <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
            Speichern, um Leistungsumfang (Subtasks) hinzuzufügen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="catalog-scope-section" style={sectionStyle}>
      <h4 style={sectionTitleStyle}>Leistungsumfang (Subtasks)</h4>

      {loading ? (
        <p style={{ color: '#888', fontSize: '13px' }}>Laden...</p>
      ) : scopeItems.length === 0 ? (
        <p style={emptyStateStyle}>
          Noch keine Subtasks. Füge Punkte hinzu, damit sie später in Angebot und Job-Checkliste erscheinen.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {scopeItems.map((item, index) => (
            <div
              key={item.id}
              data-testid={`catalog-scope-item-${item.id}`}
              style={itemRowStyle}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button
                  onClick={() => handleMoveUp(item, index)}
                  disabled={index === 0}
                  style={{
                    ...arrowButtonStyle,
                    opacity: index === 0 ? 0.3 : 1,
                  }}
                  title="Nach oben"
                >
                  <FaArrowUp size={10} />
                </button>
                <button
                  onClick={() => handleMoveDown(item, index)}
                  disabled={index === scopeItems.length - 1}
                  style={{
                    ...arrowButtonStyle,
                    opacity: index === scopeItems.length - 1 ? 0.3 : 1,
                  }}
                  title="Nach unten"
                >
                  <FaArrowDown size={10} />
                </button>
              </div>

              <input
                type="text"
                defaultValue={item.text}
                onBlur={(e) => handleUpdateText(item, e.target.value)}
                style={textInputStyle}
              />

              <button
                data-testid={`catalog-scope-delete-${item.id}`}
                onClick={() => handleDelete(item)}
                style={deleteButtonStyle}
                title="Löschen"
              >
                <FaTrash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Neuer Punkt..."
          style={{ ...textInputStyle, flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newText.trim()) {
              handleAddItem();
            }
          }}
        />
        <button
          data-testid="catalog-scope-add"
          onClick={handleAddItem}
          disabled={addingNew || !newText.trim()}
          style={{
            ...addButtonStyle,
            opacity: addingNew || !newText.trim() ? 0.5 : 1,
          }}
        >
          <FaPlus size={12} />
          <span>Punkt hinzufügen</span>
        </button>
      </div>
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  borderTop: '1px solid #e0e3ef',
  paddingTop: '16px',
  marginTop: '8px',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '14px',
  fontWeight: 600,
  color: '#666',
};

const emptyStateStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '13px',
  color: '#888',
  padding: '12px',
  background: '#f8f9fb',
  borderRadius: '6px',
  textAlign: 'center',
};

const itemRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px',
  background: '#f8f9fb',
  borderRadius: '6px',
};

const arrowButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  color: '#666',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const textInputStyle: React.CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '4px',
  fontSize: '14px',
};

const deleteButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  color: '#dc2626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const addButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  background: 'white',
  color: '#021a4a',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};
